import { useEffect, useState } from 'react'
import { ErrorDeApi } from '@nucleo/api/cliente'
import { Boton } from '@ui/primitivas/Boton'
import { Campo } from '@ui/primitivas/Campo'
import { eliminarRecurso, listarRecursos, obtenerLogo, subirLogo, subirPerfil } from './clienteDeMatrices'
import type { RecursoDeMatriz } from './tipos'

function mensaje(error: unknown): string {
  return error instanceof ErrorDeApi ? error.message : 'No fue posible guardar el archivo.'
}

export function LogoDeEmpresa({ matrizId, version }: { matrizId: string; version: number }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let vigente = true
    obtenerLogo(matrizId).then((blob) => {
      if (!vigente) return
      const lector = new FileReader()
      lector.onload = () => { if (vigente && typeof lector.result === 'string') setUrl(lector.result) }
      lector.readAsDataURL(blob)
    }).catch(() => { if (vigente) setUrl(null) })
    return () => { vigente = false }
  }, [matrizId, version])
  return url ? <img src={url} alt="Logo de la empresa" className="h-14 w-20 rounded-md border border-borde bg-superficie object-contain p-1" /> : null
}

export function CargaDeLogo({ matrizId, alCambiar }: { matrizId: string; alCambiar: () => void }) {
  const [archivo, setArchivo] = useState<File | null>(null)
  const [actual, setActual] = useState<RecursoDeMatriz | null>(null)
  const [trabajando, setTrabajando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void listarRecursos(matrizId).then((r) => setActual(r.find((x) => x.tipo === 'logo') ?? null)).catch(() => setError('No se pudo consultar el logo.')) }, [matrizId])

  const guardar = async () => {
    if (!archivo) return
    setTrabajando(true); setError(null)
    try {
      const recurso = await subirLogo(matrizId, archivo)
      setActual(recurso); setArchivo(null); alCambiar()
    } catch (e) { setError(mensaje(e)) }
    finally { setTrabajando(false) }
  }

  const quitar = async () => {
    if (!actual) return
    setTrabajando(true); setError(null)
    try { await eliminarRecurso(matrizId, actual.id); setActual(null); alCambiar() }
    catch (e) { setError(mensaje(e)) }
    finally { setTrabajando(false) }
  }

  return (
    <section className="sst-tarjeta grid gap-3 p-4 sm:p-5" aria-label="Logo de empresa">
      <div><h3 className="text-h3">Logo de la empresa</h3><p className="text-pequeno text-texto-secundario">Opcional. Aparece junto al nombre de la matriz. PNG, JPG o WebP, hasta 2 MB.</p></div>
      {actual ? <p className="text-pequeno">Actual: <strong>{actual.nombre}</strong></p> : null}
      <label className="grid gap-1 text-pequeno font-semibold">Seleccionar logo
        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => { setArchivo(e.target.files?.[0] ?? null); e.target.value = '' }} className="min-h-tactil w-full min-w-0 max-w-full rounded-md border border-borde p-2 text-pequeno" />
      </label>
      <div className="flex flex-wrap gap-2">
        <Boton compacto variante="secundario" alPulsar={() => void guardar()} deshabilitado={!archivo} trabajando={trabajando}>{actual ? 'Reemplazar logo' : 'Subir logo'}</Boton>
        {actual ? <Boton compacto variante="peligro" alPulsar={() => void quitar()} deshabilitado={trabajando}>Quitar logo</Boton> : null}
      </div>
      {error ? <p role="alert" className="text-pequeno text-critico-texto">{error}</p> : null}
    </section>
  )
}

export function PerfilesDeCargo({ matrizId, alUsar }: {
  matrizId: string; alUsar: (perfil: RecursoDeMatriz) => void
}) {
  const [perfiles, setPerfiles] = useState<RecursoDeMatriz[]>([])
  const [area, setArea] = useState('')
  const [cargo, setCargo] = useState('')
  const [archivo, setArchivo] = useState<File | null>(null)
  const [trabajando, setTrabajando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void listarRecursos(matrizId).then((r) => setPerfiles(r.filter((x) => x.tipo === 'perfil'))).catch(() => setError('No se pudieron consultar los perfiles.')) }, [matrizId])

  const guardar = async () => {
    if (!archivo || !area.trim() || !cargo.trim()) return
    setTrabajando(true); setError(null)
    try {
      const recurso = await subirPerfil(matrizId, archivo, area.trim(), cargo.trim())
      setPerfiles((previos) => [...previos, recurso]); setArchivo(null); setCargo('')
    } catch (e) { setError(mensaje(e)) }
    finally { setTrabajando(false) }
  }

  const quitar = async (perfil: RecursoDeMatriz) => {
    setError(null)
    try { await eliminarRecurso(matrizId, perfil.id); setPerfiles((previos) => previos.filter((p) => p.id !== perfil.id)) }
    catch (e) { setError(mensaje(e)) }
  }

  return (
    <details className="rounded-lg border border-borde px-4" aria-label="Perfiles de cargo del área">
      <summary className="cursor-pointer text-pequeno font-semibold">Agregar perfiles de cargo del área (opcional)</summary>
      <div className="grid gap-3 pb-4">
      <p className="text-pequeno text-texto-secundario">Sube PDF con texto, DOCX o TXT (hasta 5 MB). Revisa lo extraído y añade solo las actividades pertinentes al borrador. El archivo por sí solo no genera tareas.</p>
      <p className="text-pequeno text-texto-secundario">Solo el texto que añadas al borrador se usa al proponer. Atalaya filtra identificadores antes de procesarlo; revisa que no haya datos de personas.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Campo etiqueta="Área o sector" valor={area} alCambiar={setArea} marcador="Ej.: Embalaje" />
        <Campo etiqueta="Cargo" valor={cargo} alCambiar={setCargo} marcador="Ej.: Operador de embalaje" />
      </div>
      <label className="grid gap-1 text-pequeno font-semibold">Perfil de cargo
        <input type="file" accept=".pdf,.docx,.txt,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => { setArchivo(e.target.files?.[0] ?? null); e.target.value = '' }} className="min-h-tactil w-full min-w-0 max-w-full rounded-md border border-borde p-2 text-pequeno" />
      </label>
      <div><Boton compacto variante="secundario" alPulsar={() => void guardar()} deshabilitado={!archivo || !area.trim() || !cargo.trim()} trabajando={trabajando}>Subir perfil</Boton></div>
      {error ? <p role="alert" className="text-pequeno text-critico-texto">{error}</p> : null}
      {perfiles.length ? <ul className="grid gap-2" aria-label="Perfiles cargados">{perfiles.map((perfil) => (
        <li key={perfil.id} className="grid gap-2 rounded-md border border-borde p-3 text-pequeno">
          <div className="flex flex-wrap items-center justify-between gap-2"><strong>{perfil.cargo} · {perfil.area}</strong><span className="text-texto-secundario">{perfil.nombre}</span></div>
          <details><summary className="cursor-pointer font-semibold">Revisar texto extraído</summary><p className="mt-2 whitespace-pre-wrap text-texto-secundario">{perfil.texto}</p></details>
          <div className="flex flex-wrap gap-2">
            <Boton compacto variante="secundario" alPulsar={() => alUsar(perfil)}>Añadir al borrador</Boton>
            <Boton compacto variante="peligro" alPulsar={() => void quitar(perfil)}>Quitar perfil</Boton>
          </div>
        </li>
      ))}</ul> : null}
      </div>
    </details>
  )
}
